import { LocationsFeaturesResponse } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { DentalInsuranceReportTypes } from '@frontend/api-insurance-eligibility-report';
import { isEmpty } from '@frontend/validation';
import {
  TableDataTypeExcludeTuuthFairy,
  TablesDataType,
  TuuthFairyAndDXCTableKeyTypes,
  TuuthFairyTableType,
} from '../types/types';

export const isEnhancedVerificationFeatureActive = (
  customizationFlagsData: LocationsFeaturesResponse | undefined,
  locationId: string
) => {
  return (
    customizationFlagsData?.locationsFeatures &&
    customizationFlagsData?.locationsFeatures[locationId].features?.find(
      (feat) => feat.featureEnum === 'ENHANCED_INSURANCE_VERIFICATION'
    )?.state === 'ACTIVE'
  );
};

export const segregateByNetwork = (data: TablesDataType) => {
  const result = {
    in_network: {} as TablesDataType,
    out_network: {} as TablesDataType,
  };
  for (const key in data) {
    //This can be maximum, limitation, deductible.. table data
    const tableData = data[key as keyof TablesDataType];

    if (Array.isArray(tableData)) {
      // Initialize arrays if not already present
      result.in_network[key as keyof TablesDataType] = [];
      result.out_network[key as keyof TablesDataType] = [];

      //This caching is for typescript so typeScript knows they exist
      const inNetworkArray = result.in_network[key as keyof TablesDataType]!;
      const outNetworkArray = result.out_network[key as keyof TablesDataType]!;

      (tableData || []).forEach((item) => {
        const network =
          (item as DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo).network ||
          (item as TuuthFairyTableType).networkType;

        const isOtherNetwork = network?.toLowerCase()?.includes('other');

        //checking if it is in-network
        if (network?.toLowerCase().includes('in')) {
          /**
           * Below if else condition is purely for satisfying typescript as we have two types for maximum and deductible DentalXChangePlanBenefitInfo | TuuthFairyTableType there are few type discrepencies
           * This is not affecting the performance of the function
           */
          if (['maximum', 'deductible'].includes(key)) {
            (inNetworkArray as (DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo | TuuthFairyTableType)[]).push(
              item
            );
          } else {
            (inNetworkArray as DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo[]).push(
              item as DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo
            );
          }
        } else if (isEmpty(network) || isOtherNetwork) {
          (inNetworkArray as (DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo | TuuthFairyTableType)[]).push(
            item
          );
          (outNetworkArray as (DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo | TuuthFairyTableType)[]).push(
            item
          );
        } else {
          if (['maximum', 'deductible'].includes(key)) {
            (outNetworkArray as (DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo | TuuthFairyTableType)[]).push(
              item
            );
          } else {
            (outNetworkArray as DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo[]).push(
              item as DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo
            );
          }
        }
      });
    }
  }

  return result;
};

export const filterTableDataBySearchTermAndNetworkType = (searchTerm: string, tablesData: TablesDataType) => {
  const filteredTablesData: TablesDataType = {};
  for (const table in tablesData) {
    const tableData = tablesData[table as keyof TablesDataType];

    const lowerCasedSearchTerm = searchTerm.trim().toLowerCase();

    /**
     * Below if else condition is purely for satisfying typescript as we have two types for maximum and deductible DentalXChangePlanBenefitInfo | TuuthFairyTableType there are few type discrepencies
     * This is not affecting the performance of the function
     */
    if (['maximum', 'deductible'].includes(table)) {
      filteredTablesData[table as TuuthFairyAndDXCTableKeyTypes] = (tableData || []).filter((item) => {
        if (!!lowerCasedSearchTerm) {
          return Object.values(item).some((value) => String(value).toLowerCase().includes(lowerCasedSearchTerm));
        } else {
          // Returning true when there is no search term because user didn't search for anything and want to see all the data
          return true;
        }
      });
    } else {
      const safeTableData = tableData as DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo[];

      filteredTablesData[table as TableDataTypeExcludeTuuthFairy] = safeTableData.filter(
        (item): item is DentalInsuranceReportTypes.DentalXChangePlanBenefitInfo => {
          if (!!lowerCasedSearchTerm) {
            return Object.values(item).some((value) => String(value).toLowerCase().includes(lowerCasedSearchTerm));
          } else {
            // Returning true when there is no search term because user didn't search for anything and want to see all the data
            return true;
          }
        }
      );
    }
  }
  return filteredTablesData;
};

export const reorderByPriority = <T extends string>(arr: T[], priority: T[]): T[] => {
  const copy = [...arr]; // avoiding mutating original arr
  const reordered: T[] = [];

  priority.forEach((item) => {
    const index = copy.indexOf(item);
    if (index !== -1) {
      reordered.push(item);
      copy.splice(index, 1); // removing from copy as we are merging copy while returning
    }
  });

  return [...reordered, ...copy];
};
